import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'

/**
 * Dönüşüm Yönetimi — Dönüşüm Anlatısı, TMO Kurulumu ve TMO Danışmanlığı.
 *
 * TMO kısaltması İngilizce açılımıyla korunuyor (Transformation Management
 * Office): Türk kurumlarında PMO gibi yerleşik bir kısaltma, ve "DYO" diye bir
 * şey yok. Aynı sebeple KPI, P&L ve OKR da çevrilmedi.
 */
export const transformationManagement: WrittenPage = {
  navLabel: 'Dönüşüm Yönetimi',
  title: 'Dönüşüm Yönetim Ofisi (TMO)',
  description:
    'Dönüşüm anlatısı, TMO kurulumu ve TMO danışmanlığı. Bir niyetler portföyünü, değeri görülebilen yönetilmiş bir uygulamaya çevirin.',
  sections: [
    {
      kind: 'hero',
      headline:'Dönüşümler nadiren hırs eksikliğinden başarısız olur. Parçalanmadan olur.',
      sub: 'Ajan tabanlı yapay zeka yolculuğunuzu güvenceye alın ve hızlandırın. Anlatıyı kurar, ofisi ayağa kaldırır ve ritmi tutmak için kalırız.',
      photo: {
        src: '/photo/factory-roadmap.jpg',
        alt: 'Bir Digitopia danışmanı, üretim alanındaki bir ekibe dönüşüm yol haritasını anlatıyor; büyüme, operasyonel verimlilik ve teknoloji programları çeyreklik bir zaman çizgisinde.',
      },
      ctas: [{ label: 'Bize Ulaşın', href: '#contact' }],
    },
    {
      kind: 'list',
      heading: 'Bu şekli daha önce gördünüz.',
      variant: 'steps',
      items: [
        'Strateji bir şey söyler.',
        'Bütçe başka bir şey söyler.',
        'Operasyon modeli ikisine de direnir.',
        'İnsanlar zaten fazla yüklü.',
        'Yönetişim ya çok zayıf ya çok yavaş.',
        'Ve değişim portföyü, yarım kalmış niyetlerin mezarlığına döner.',
      ],
      body: 'Dönüşüm ihtiyacını yapay zeka yaratmadı. Onun hızını, kapsamını, birbirine bağımlılığını ve sonuçlarını büyüttü. Eskiden bir teknoloji programı olan şey artık kurumsal bir meseledir; strateji, yönetişim, operasyon modeli, veri, risk, yetenek ve kültür birlikte hareket etmek zorundadır.',
    },
    {
      kind: 'list',
      heading: 'Dönüşüm Anlatısı.\nAdını koyun, yoksa kimse peşinden gitmez.',
      intro:
        'Çoğu dönüşümün sunumu var, hikâyesi yok. Bir anlatı; değişime insanların tanıdığı bir ad, muğlak bir hedef yerine tanımlı bir hedef durum, sorumluluğu üstlenen bir sahip ve günlük işle temas ettiğinde ayakta kalan bir gerekçe verir.',
      variant: 'check',
      items: [
        'Dönüşümünüzün insanların tanıdığı bir adı var mı?',
        'Yalnızca bir hırs değil, net bir hedef durumu var mı?',
        'Sorumluluğu üstlenen bir yönetici sahibi var mı?',
        'İş, teknoloji, finans, İK ve risk boyunca bir yol arkadaşlığı kurdunuz mu?',
        'Öncelikleriniz, düşük değerli işi durduracak kadar açık ve acımasız mı?',
        'Organizasyonunuzun insanlarını yakmadan ne kadar değişim emebileceğini biliyor musunuz?',
      ],
      body: 'Bu soruların birkaçına yanıtınız "tam değil", "sayılır" ya da "duruma göre" ise sorun uygulamada değil. Sorun dönüşüm tasarımında.',
      cta: { label: 'Transformation Readiness Check', href: LINKS.appetiteTool },
    },
    {
      kind: 'features',
      heading: 'TMO Kurulumu. Makine dairesi.',
      sub: 'Gerçek bir TMO, adı değişmiş bir PMO değildir; bir raporlama fabrikası da değildir. Küçük, itibarlı, fonksiyonlar arası ve karar merciine yakındır.',
      columns: 3,
      textSize: 'lg',
      items: [
        {
          title: 'Portföyü yönetin',
          body: 'Her dönüşüm girişiminin tek bir görünümü ve onları sıraya koyma yetkisi.',
        },
        {
          title: 'Yönetici kararlarını mümkün kılın',
          body: 'Kararlar çerçevelenmiş gelir; ödünleşim daha masaya gelmeden açık edilmiştir.',
        },
        {
          title: 'İlerlemeyi ve riski izleyin',
          body: 'Performans ve KPI takibi, bağımlılıklar ve eskalasyon.',
        },
        {
          title: 'Değişim yönetimini içeri alın',
          body: 'İletişim ve benimseme, uygulamanın sonuna eklenen değil içinde olan bir iş.',
        },
        {
          title: 'Odağı ve kapasiteyi koruyun',
          body: 'Organizasyonun emebileceği bir sınır var. Birinin o çizgiyi tutması gerekir.',
        },
        {
          title: 'Değerin gerçekleşmesini destekleyin',
          body: 'Finans, teslimatın yanında durur; fayda varsayılmaz, izlenir.',
        },
      ],
      cta: { label: 'Bize Ulaşın', href: '#contact' },
    },
    {
      kind: 'photo',
      photo: {
        src: '/photo/team-high-five-office.png',
        alt: 'Aydınlık bir ofiste küçük bir masanın etrafında toplanmış dört meslektaş; açık bir dizüstü bilgisayarın üzerinde eller çakışıyor.',
      },
    },
    {
      kind: 'list',
      heading: 'TMO Danışmanlığı. Ürün, ritmin kendisidir.',
      intro:
        'Dönüşüm liderliğinizin yanında dururuz, yararı varsa zor gözden geçirmelere başkanlık ederiz ve rahatsız edici olduğunda eskale ederiz. Buradaki yönetişim bürokrasi değil, sistem tasarımıdır. Altındaki mekanizmayı Navigator Platform çalıştırır: panolar, bağımlılıklar, fayda takibi ve toplantı sırasında değil öncesinde hazırlanan gözden geçirme paketi.',
      items: [
        'Çeyreklik iş gözden geçirmeleri',
        'Aylık yönlendirme toplantıları',
        'Girişim düzeyinde panolar',
        'Bağımlılık ve risk eskalasyonu',
        'Fayda takibi',
        'Kayda geçen ve takibi yapılan kararlar',
      ],
      cta: { label: 'Bize Ulaşın', href: '#contact' },
    },
    {
      // İngilizcesiyle aynı bölüm: Strategy Consulting'deki sadeleştirilmiş
      // Hall of Fame kartı, şirket başına bir sekme. Üç alıntı da bu sayfanın
      // konusuna — ritim ve yönetişime — bakıyor.
      kind: 'quotes',
      variant: 'minimal-carousel',
      heading: 'Başarı Hikâyeleri',
      items: [
        {
          company: 'Borusan Otomotiv',
          logo: { name: 'Borusan Otomotiv', src: '/logo/borusanotomotivcard.png' },
          portrait: { src: '/photo/portrait-asli-de-munnik.png', alt: 'Aslı de Munnink' },
          quote: [
            'Dijital olgunluk, yılda bir kez değerlendirilip sonra rafa kaldırılacak bir iş '
            + 'değil. Aksiyonlarımızı üç ayda ve altı ayda bir değerlendirmek, ilerlememizi '
            + 'görmemizi sağlıyor ve uzun vadeli hedeflerimiz için bizi daha da motive ediyor.',
          ],
          source: 'Aslı de Munnink, CDO',
          cta: { label: 'Devamını okuyun', href: '/en/customers/borusan-otomotiv-grup' },
        },
        {
          company: 'Petrol Ofisi',
          logo: { name: 'Petrol Ofisi', src: '/logo/petrolofisicard.png' },
          portrait: { src: '/photo/portrait-mehmet-ali-gulsen.png', alt: 'Mehmet Ali Gülşen' },
          quote: [
            'Digitopia ile çalışıyoruz çünkü alanının en iyisi ve iş birliğimize müşteri odaklı '
            + 'bir yaklaşım getiriyor. İlerlememizi üç ayda bir ölçmek ve bize özel dijital yol '
            + 'haritaları hazırlamak gibi sürekli destekleri bizim için çok değerli oldu.',
            'Çalışanlarımızı dijitalleşme yolculuğuna dahil ederek ve aktif katılımlarını '
            + 'teşvik ederek Digitopia, dönüşüm için sağlam bir zemin kurmamıza yardımcı oldu.',
          ],
          source: 'Mehmet Ali Gülşen, Chief Digital Transformation Officer',
          cta: { label: 'Devamını okuyun', href: '/en/customers/petrol-ofisi-group' },
        },
        {
          company: 'ebebek',
          logo: { name: 'ebebek', src: '/logo/ebebekcard.png' },
          portrait: { src: '/photo/portrait-baris-simsek.png', alt: 'Barış Şimşek' },
          quote: [
            'Kendimizi sektör ve global kıyaslamalarla karşılaştırarak geçmişte nerede '
            + 'olduğumuzu, bugün nerede durduğumuzu ve gelecekte nereye gitmemiz gerektiğini '
            + 'net biçimde görebiliyoruz.',
            'Periyodik değerlendirmelerle doğru yolda olduğumuzdan emin oluyor ve gerekli '
            + 'aksiyonları buna göre alıyoruz.',
          ],
          source: 'Barış Şimşek, CTO',
          cta: { label: 'Devamını okuyun', href: '/en/customers/ebebek' },
        },
      ],
      cta: { label: 'Başarı hikâyelerini görün', href: LINKS.successStories },
      check:
        'Bu bölümdeki üç alıntı da İngilizce yayımlanmış sözlerin Türkçeye çevirisidir; '
        + 'yayımlanmadan önce onay ister.',
    },
  ],
}
